import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AutoComplete, Input, Tag, Typography } from 'antd';
import {
  SearchOutlined,
  ProjectOutlined,
  FileProtectOutlined,
  TeamOutlined,
  FunnelPlotOutlined,
  ShoppingOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMockStore } from '@/mocks/store';
import { selectSearchResults, type SearchGroup, type SearchResult } from '@/mocks/selectors';
import { semantic } from '@/theme/tokens';

const { Text } = Typography;

interface OptionLeaf {
  value: string;
  label: ReactNode;
  data: SearchResult;
}

interface OptionGroup {
  label: ReactNode;
  options: OptionLeaf[];
}

/**
 * Header global search — Sprint 4 rewrite (S4-T03).
 *
 * Branded multi-entity AutoComplete that mirrors the CommandPalette's
 * visual rhythm: brand-coloured icon chips per group, secondary detail
 * per row, group counts in the header. Five per group (was three).
 *
 * Keyboard:
 *   - ↑/↓ traverses highlighted options (AntD AutoComplete owns this)
 *   - Enter on a highlighted row navigates and clears
 *   - Esc clears the query and closes the popover
 *
 * The cmdk palette (⌘K) is the universal jump-to-anything tool; this
 * header search is the "type-while-thinking" path. The two share the
 * same colour palette and icons so they read as one system.
 */

const GROUP_META: Record<
  SearchGroup,
  { color: string; icon: ReactNode }
> = {
  projects: { color: semantic.status.success, icon: <ProjectOutlined /> },
  contracts: { color: semantic.brand.accent, icon: <FileProtectOutlined /> },
  employees: { color: semantic.status.info, icon: <TeamOutlined /> },
  suppliers: { color: '#8A4A1F', icon: <ShoppingOutlined /> },
  leads: { color: '#9C3961', icon: <FunnelPlotOutlined /> },
};

const GROUP_ORDER: SearchGroup[] = ['projects', 'contracts', 'employees', 'suppliers', 'leads'];

export default function GlobalSearch() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const state = useMockStore();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  const results: SearchResult[] = useMemo(
    () => selectSearchResults(state, query, 5),
    [state, query],
  );

  const grouped = useMemo(() => {
    const map = new Map<SearchGroup, SearchResult[]>();
    for (const r of results) {
      const list = map.get(r.group) ?? [];
      list.push(r);
      map.set(r.group, list);
    }
    return map;
  }, [results]);

  const options: OptionGroup[] = useMemo(() => {
    return GROUP_ORDER.filter((g) => (grouped.get(g)?.length ?? 0) > 0).map<OptionGroup>(
      (g) => {
        const meta = GROUP_META[g];
        const groupResults = grouped.get(g) ?? [];
        return {
          label: (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                color: meta.color,
                padding: '4px 0',
              }}
            >
              <span style={{ color: meta.color }}>{meta.icon}</span>
              {t(`header.searchGroup.${g}`)}
              <Text type="secondary" style={{ fontSize: 10, fontWeight: 400, marginInlineStart: 'auto' }}>
                {groupResults.length}
              </Text>
            </div>
          ),
          options: groupResults.map<OptionLeaf>((r) => ({
            value: r.id,
            label: (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '4px 0',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 6,
                    background: `${meta.color}15`,
                    color: meta.color,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    flexShrink: 0,
                  }}
                >
                  {meta.icon}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'rgba(26, 31, 27, 0.9)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.label}
                  </div>
                  {r.secondary && (
                    <div
                      style={{
                        fontSize: 11,
                        color: 'rgba(26, 31, 27, 0.5)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginTop: 1,
                      }}
                    >
                      {r.secondary}
                    </div>
                  )}
                </div>
              </div>
            ),
            data: r,
          })),
        };
      },
    );
  }, [grouped, t]);

  const handleSelect = (_value: string, option: OptionLeaf) => {
    if (!option?.data) return;
    navigate(option.data.to);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Escape') {
      setQuery('');
      setOpen(false);
    }
  };

  const hasResults = results.length > 0;
  const showEmpty = query.trim().length > 0 && !hasResults;
  const showHint = query.trim().length === 0 && open;

  return (
    <div ref={wrapperRef} style={{ width: 280 }}>
      <AutoComplete
        value={query}
        // AntD's AutoComplete typing infers a flat array; cast the grouped
        // structure it accepts at runtime.
        options={options as unknown as OptionLeaf[]}
        onChange={(v: string) => {
          setQuery(v);
          setOpen(true);
        }}
        onSelect={(value: string, option) => handleSelect(value, option as OptionLeaf)}
        onFocus={() => setOpen(true)}
        open={open && (hasResults || showEmpty || showHint)}
        notFoundContent={
          showEmpty ? (
            <div style={{ padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('search.noResults', { query })}
              </Text>
              <Text style={{ fontSize: 11, color: semantic.brand.primary }}>
                <ThunderboltOutlined style={{ marginInlineEnd: 4 }} />
                {t('search.tryCmdk', 'Try ⌘K for a wider search across pages and recents')}
              </Text>
            </div>
          ) : showHint ? (
            <div
              style={{
                padding: '12px 8px',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('search.hint', 'Type to search projects, contracts, people, suppliers, leads…')}
              </Text>
              <Text style={{ fontSize: 11, color: 'rgba(26, 31, 27, 0.5)' }}>
                {t('search.hintCmdk', 'Tip: ⌘K opens the universal palette with pages and recents.')}
              </Text>
            </div>
          ) : null
        }
        style={{ width: '100%' }}
        popupMatchSelectWidth={360}
      >
        <Input
          placeholder={t('header.search')}
          prefix={<SearchOutlined style={{ color: semantic.brand.primary }} />}
          allowClear
          onKeyDown={handleKeyDown}
          suffix={
            <Tag
              style={{
                marginInlineStart: 4,
                fontSize: 10,
                background: 'rgba(184, 137, 62, 0.12)',
                color: semantic.brand.accent,
                borderColor: 'rgba(184, 137, 62, 0.32)',
              }}
            >
              ⌘K
            </Tag>
          }
        />
      </AutoComplete>
    </div>
  );
}
